import React, { useEffect, useState, useMemo } from 'react';
import { Link } from 'react-router-dom';

import api from '../../api/axios';
import { useAuth } from '../../context/AuthContext';

const PAGE_SIZE = 10;

const KamHome = () => {
  const { user } = useAuth();
  const [enterprises, setEnterprises] = useState([]);
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [sortBy, setSortBy] = useState('open'); // 'open' | 'name' | 'total'

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [entRes, tickRes] = await Promise.all([
          api.get(`/api/kam/${user.userId}/enterprises`),
          api.get(`/api/kam/${user.userId}/tickets`),
        ]);
        setEnterprises(entRes.data);
        setTickets(tickRes.data);
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [user.userId]);

  const getStats = (eid) => {
    const ent = tickets.filter(t => t.enterpriseId === eid);
    return {
      total: ent.length,
      open: ent.filter(t => t.status === 'OPEN').length,
      inProgress: ent.filter(t => t.status === 'IN_PROGRESS').length,
      resolved: ent.filter(t => t.status === 'RESOLVED').length,
    };
  };

  // Overall stats
  const totalOpen = tickets.filter(t => t.status === 'OPEN').length;
  const totalInProgress = tickets.filter(t => t.status === 'IN_PROGRESS').length;
  const totalResolved = tickets.filter(t => t.status === 'RESOLVED').length;

  // Enterprises enriched with stats
  const enriched = useMemo(() =>
    enterprises.map(ent => ({ ...ent, stats: getStats(ent.enterpriseId) })),
    [enterprises, tickets]
  );

  // Needs Attention: have open tickets, sorted by open count desc (top 5)
  const needsAttention = useMemo(() =>
    enriched
      .filter(e => e.stats.open > 0)
      .sort((a, b) => b.stats.open - a.stats.open)
      .slice(0, 5),
    [enriched]
  );

  // Filtered + sorted list for table
  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    const list = enriched.filter(e =>
      (e.enterpriseName ?? '').toLowerCase().includes(q) ||
      (e.enterpriseId ?? '').toLowerCase().includes(q)
    );
    if (sortBy === 'open') return [...list].sort((a, b) => b.stats.open - a.stats.open);
    if (sortBy === 'total') return [...list].sort((a, b) => b.stats.total - a.stats.total);
    return [...list].sort((a, b) => a.enterpriseName.localeCompare(b.enterpriseName));
  }, [enriched, search, sortBy]);

  const totalPages = Math.ceil(filtered.length / PAGE_SIZE);
  const paged = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const handleSearch = (e) => {
    setSearch(e.target.value);
    setPage(1);
  };

  const urgencyColor = (open, total) => {
    if (open === 0) return 'bg-green-100 text-green-700';
    const ratio = open / (total || 1);
    if (ratio > 0.5) return 'bg-red-100 text-red-700';
    if (ratio > 0.2) return 'bg-amber-100 text-amber-700';
    return 'bg-blue-100 text-blue-700';
  };

  return (
    <div className="min-h-screen bg-gray-50">
      
      <div className="max-w-7xl mx-auto px-4 py-8">

        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-800">Welcome, {user.name}</h1>
            <p className="text-gray-500 text-sm">
              Managing {enterprises.length} enterprise{enterprises.length !== 1 ? 's' : ''}
            </p>
          </div>
          <Link to="/kam/raise"
            className="bg-primary text-white px-5 py-2.5 rounded-lg hover:bg-primary-light transition-colors font-medium text-sm">
            + Raise Ticket
          </Link>
        </div>

        {/* Summary Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          {[
            { label: 'Total Tickets', value: tickets.length, color: 'bg-blue-50 text-blue-700', sub: `across ${enterprises.length} enterprises` },
            { label: 'Open', value: totalOpen, color: 'bg-red-50 text-red-700', sub: 'require action' },
            { label: 'In Progress', value: totalInProgress, color: 'bg-amber-50 text-amber-700', sub: 'being handled' },
            { label: 'Resolved', value: totalResolved, color: 'bg-green-50 text-green-700', sub: 'completed' },
          ].map(s => (
            <div key={s.label} className={`rounded-xl p-5 ${s.color}`}>
              <p className="text-sm font-medium opacity-70">{s.label}</p>
              <p className="text-3xl font-bold mt-1">{s.value}</p>
              <p className="text-xs opacity-60 mt-1">{s.sub}</p>
            </div>
          ))}
        </div>

        {/* Needs Attention */}
        {!loading && needsAttention.length > 0 && (
          <div className="mb-8">
            <div className="flex items-center gap-2 mb-3">
              <span className="w-2 h-2 rounded-full bg-red-500 animate-pulse" />
              <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">
                Needs Attention ({needsAttention.length} enterprises with open tickets)
              </h2>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
              {needsAttention.map(ent => (
                <div key={ent.enterpriseId}
                  className="bg-white border border-red-100 rounded-xl p-4 hover:shadow-md transition-shadow">
                  <p className="font-semibold text-gray-800 text-sm truncate" title={ent.enterpriseName}>
                    {ent.enterpriseName}
                  </p>
                  <p className="text-xs text-gray-400 mb-3 truncate" title={ent.enterpriseId}>{ent.enterpriseId}</p>
                  <div className="flex items-center justify-between">
                    <span className="text-2xl font-bold text-red-600">{ent.stats.open}</span>
                    <span className="text-xs text-gray-400">open</span>
                  </div>
                  {/* Progress bar: open ratio */}
                  <div className="mt-2 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-red-400 rounded-full"
                      style={{ width: `${Math.min(100, (ent.stats.open / (ent.stats.total || 1)) * 100)}%` }}
                    />
                  </div>
                  <Link to={`/kam/raise?enterprise=${ent.enterpriseId}`}
                    className="block mt-3 text-center text-xs text-primary hover:text-primary-dark font-medium">
                    Raise Ticket →
                  </Link>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* All Enterprises Table */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100">
          {/* Table Header / Controls */}
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 p-4 border-b border-gray-100">
            <div>
              <h2 className="text-base font-semibold text-gray-700">All Enterprises</h2>
              <p className="text-xs text-gray-400">
                {filtered.length} of {enterprises.length} shown
              </p>
            </div>
            <div className="flex items-center gap-2 w-full sm:w-auto">
              <input
                type="text"
                placeholder="Search by name or ID..."
                value={search}
                onChange={handleSearch}
                className="border border-gray-200 rounded-lg px-3 py-1.5 text-sm w-full sm:w-56 focus:outline-none focus:ring-2 focus:ring-primary/30"
              />
              <select
                value={sortBy}
                onChange={e => { setSortBy(e.target.value); setPage(1); }}
                className="border border-gray-200 rounded-lg px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 bg-white"
              >
                <option value="open">Sort: Open</option>
                <option value="total">Sort: Total</option>
                <option value="name">Sort: Name</option>
              </select>
            </div>
          </div>

          {/* Table */}
          {loading ? (
            <div className="text-center py-16 text-gray-400 text-sm">Loading...</div>
          ) : paged.length === 0 ? (
            <div className="text-center py-16 text-gray-400 text-sm">No enterprises found.</div>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="text-xs text-gray-400 uppercase tracking-wide bg-gray-50">
                  <th className="text-left px-4 py-3 font-medium">Enterprise</th>
                  <th className="text-center px-4 py-3 font-medium">Total</th>
                  <th className="text-center px-4 py-3 font-medium">Open</th>
                  <th className="text-center px-4 py-3 font-medium">In Progress</th>
                  <th className="text-center px-4 py-3 font-medium">Resolved</th>
                  <th className="text-center px-4 py-3 font-medium">Status</th>
                  <th className="text-center px-4 py-3 font-medium">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {paged.map(ent => {
                  const s = ent.stats;
                  const badge = urgencyColor(s.open, s.total);
                  const resolvedPct = s.total > 0 ? Math.round((s.resolved / s.total) * 100) : 100;
                  return (
                    <tr key={ent.enterpriseId} className="hover:bg-gray-50 transition-colors">
                      <td className="px-4 py-3">
                        <p className="font-medium text-gray-800">{ent.enterpriseName}</p>
                        <p className="text-xs text-gray-400">{ent.enterpriseId}</p>
                      </td>
                      <td className="px-4 py-3 text-center font-semibold text-gray-700">{s.total}</td>
                      <td className="px-4 py-3 text-center font-semibold text-red-600">{s.open}</td>
                      <td className="px-4 py-3 text-center font-semibold text-amber-600">{s.inProgress}</td>
                      <td className="px-4 py-3 text-center font-semibold text-green-600">{s.resolved}</td>
                      <td className="px-4 py-3 text-center">
                        <div className="flex flex-col items-center gap-1">
                          <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${badge}`}>
                            {s.open === 0 ? 'All Clear' : `${s.open} Open`}
                          </span>
                          <div className="w-16 h-1 bg-gray-100 rounded-full overflow-hidden">
                            <div
                              className="h-full bg-green-400 rounded-full"
                              style={{ width: `${resolvedPct}%` }}
                            />
                          </div>
                          <span className="text-xs text-gray-400">{resolvedPct}% resolved</span>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <Link
                          to={`/kam/raise?enterprise=${ent.enterpriseId}`}
                          className="inline-block text-xs text-primary hover:text-primary-dark font-medium border border-primary/20 px-3 py-1 rounded-lg hover:bg-primary/5 transition-colors"
                        >
                          Raise Ticket
                        </Link>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}

          {/* Pagination */}
          {!loading && totalPages > 1 && (
            <div className="flex items-center justify-between px-4 py-3 border-t border-gray-100">
              <p className="text-xs text-gray-400">
                Page {page} of {totalPages} &middot; {filtered.length} enterprises
              </p>
              <div className="flex items-center gap-1">
                <button
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={page === 1}
                  className="px-3 py-1.5 text-xs rounded-lg border border-gray-200 disabled:opacity-40 hover:bg-gray-50 transition-colors"
                >
                  Previous
                </button>
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  const start = Math.max(1, Math.min(page - 2, totalPages - 4));
                  const p = start + i;
                  return p <= totalPages ? (
                    <button
                      key={p}
                      onClick={() => setPage(p)}
                      className={`px-3 py-1.5 text-xs rounded-lg border transition-colors ${
                        p === page
                          ? 'bg-primary text-white border-primary'
                          : 'border-gray-200 hover:bg-gray-50'
                      }`}
                    >
                      {p}
                    </button>
                  ) : null;
                })}
                <button
                  onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                  className="px-3 py-1.5 text-xs rounded-lg border border-gray-200 disabled:opacity-40 hover:bg-gray-50 transition-colors"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>

      </div>
    </div>
  );
};

export default KamHome;
